import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { useFetchBooking } from "../../../api/queries/bookingQueries";
import { getChangeRequestData } from "../../../components/detail/getChangeRequestData";
import { SquarePayPopup } from "../../../components/square-pay/SquarePayPopup";
import { UiTextXs } from "../../../components/text/UiText";
import { UiH4 } from "../../../components/text/UiTitle";
import { SHOPPING_CART_MODE } from "../../../constants";
import { useAuth } from "../../../context/AuthContext";
import { useSearchParamsOfBooking } from "../../../hooks/searchParams/useSearchParamsOfBooking";
import { useCartLocalStorage } from "../../../hooks/useCartLocalStorage";
import { mappingBookingState2RequestDTO } from "../../../mapping/mappingBookingState2RequestDTO";
import BookingService from "../../../services/bookingService";
import UserService from "../../../services/userService";
import { COUNTRY_CODE } from "../../../utils/country-code";
import { TUseOrderProduct } from "../useOrderProduct";
import { VoucherBox } from "../VoucherBox";
import { CartPriceDetail } from "./CartPriceDetail";
import PopupAgree from "./PopupAgree";
import { usePopup } from "../../../hooks/usePopup";
import { Trans } from "react-i18next";
import ErrorPopup from "../../../components/toast/ErrorPopup";
import { icAsset } from "../../../components/image/image-src";

const useBillingAddressData = (
  mode: string,
  cartItems: TUseOrderProduct["cartItems"],
) => {
  const { isAuthenticated, setShowLoginModal } = useAuth();
  const billingAddress = useQuery({
    queryFn: () => UserService.getBillingAddress(),
    enabled: isAuthenticated && mode !== SHOPPING_CART_MODE.UPDATE_BOOKING,
  });

  const { data: bookingData, refetch } = useFetchBooking(
    mode === SHOPPING_CART_MODE.UPDATE_BOOKING ? cartItems[0]?.id : undefined,
  );
  const customerData = bookingData?.customer;
  if (!customerData && mode === SHOPPING_CART_MODE.UPDATE_BOOKING) {
    refetch();
  }

  const billingAddressData =
    mode !== SHOPPING_CART_MODE.UPDATE_BOOKING
      ? billingAddress.data
      : (function () {
          if (customerData) {
            return {
              lastName: customerData.lastName,
              firstName: customerData.firstName,
              koreanFirstName: customerData.koreanFirstName,
              koreanLastName: customerData.koreanLastName,
              phoneNumber: customerData.phone,
              email: customerData.email,
              confirmEmail: customerData.email,
              address: customerData.address,
              callingCode: customerData.callingCode,
            };
          }
          return null;
        })();

  return billingAddressData as BillingAddressDTO | null;
};

const FormBookOrderProduct = (jj: TUseOrderProduct) => {
  const [searchParams] = useSearchParams();
  const mode = (searchParams.get("mode") ?? "").toString();
  const cartItems = jj.cartItems;

  const billingAddressData = useBillingAddressData(mode, cartItems);

  return (
    <>
      {mode === SHOPPING_CART_MODE.UPDATE_BOOKING &&
        billingAddressData?.firstName && (
          <style>{`.form-1 > div:not(.term-box) {
          pointer-events: none;
          opacity: 0.5;
        }`}</style>
        )}
      <FormBookOrderProductBody jj={jj} billingAddress={billingAddressData} />
    </>
  );
};
const FormBookOrderProductBody = ({
  jj,
  billingAddress,
}: {
  jj: TUseOrderProduct;
  billingAddress?: BillingAddressDTO | null;
}) => {
  const { t } = useTranslation();
  const bsp = useSearchParamsOfBooking();
  const isPass =
    jj.cartItems?.length === 1 &&
    jj.cartItems[0]?.product?.productType === "PASS";

  const [isPopupVisible, setPopupVisible] = useState(false);
  const agreePopup = usePopup();
  const agreePopup2 = usePopup();
  const errorBookingPopup = usePopup();
  const [isActiveInfoBank, setisActiveInfoBank] = useState(false);
  const { isAuthenticated, setShowLoginModal, isAdminRole } = useAuth();
  const [payProcessing, setPayProcessing] = useState(false);

  const [errorTitle, setErrorTitle] = useState("");
  const [errorDescription, setErrorDescription] = useState("");

  const [isBooking, setIsBooking] = useState(false);

  const handleOpenLogin = () => {
    setShowLoginModal(true);
  };
  const [searchParams] = useSearchParams();

  const cartItems = jj.cartItems;

  const mode = (searchParams.get("mode") ?? "").toString();
  const productIds: number[] = cartItems
    .map((x) => x?.product?.id || 0)
    .filter((x) => x);
  // const cartItems = [];

  // useEffect(() => {
  //   if (searchParams.get("upgrade")) {
  //     setIsUpgrade(true);
  //     BookingService.getBooking(Number(cartItems[0]?.bookingInfo?.id)).then(
  //       (res) => {
  //         setFormValues((prev) => ({ ...prev, ...res.paymentDTO }));
  //         setUserForm((prev) => ({ ...prev, ...res.customer }));
  //       }
  //     );
  //   }
  //   setDiscount(
  //     cartItems
  //       .filter((card) => card.checked)
  //       .reduce(
  //         (acc, card) =>
  //           acc + PriceService.getPrice(card.bookingInfo).totalPrice,
  //         0
  //       )
  //   );
  //   console.log("discount " + discount);
  // }, []);
  const queryClient = useQueryClient();
  const redirectToBooking = () => {
    queryClient.invalidateQueries({
      queryKey: ["booking"],
    });
    console.log("redirectToBooking");
    if (isAuthenticated) {
      window.location.href = "/mypage/booking";
      return;
    }
    window.location.href =
      "/mypage/booking?bookIds=" + cartItems.map((x) => x.id).join(",");
  };

  const createBooking = useMutation(
    async (data?: BookingRequest) => {
      if (!data) throw new Error("User info is not available");
      return await BookingService.saveBooking(data);
    },
    {
      onSuccess: (data) => {
        console.log("Update successful:", data);
        // if (data && data[0]) {
        //   onBookingSuccess &&
        //     onBookingSuccess({
        //       id: data[0]?.booking.bookNo,
        //       date: data[0]?.booking.bookTime,
        //       status: data[0]?.booking.status,
        //       email: data?.email,
        //     });
        // }
        redirectToBooking();
      },
      onError: (error: any) => {
        console.error("Update failed:", error);
        alert(
          t(
            "An error occurred while processing your booking. " +
              (error?.message ?? ""),
          ),
        );
      },
    },
  );
  const cartLocalStorage = useCartLocalStorage();
  const saveBooking = useMutation(
    (request: AllBookingRequestDTO) => {
      return BookingService.save(
        {
          ...request,
          isUpgrade: bsp.isUpgrade,
          isEdit: bsp.isEdit,
        },
        mode === SHOPPING_CART_MODE.UPDATE_BOOKING,
      );
    },
    {
      onMutate: () => setIsBooking(true),
      onSuccess: (data) => {
        console.log("Update successful:", data);
        // if (data && data[0]) {
        //   onBookingSuccess &&
        //     onBookingSuccess({
        //       id: data[0]?.booking.bookNo,
        //       date: data[0]?.booking.bookTime,
        //       status: data[0]?.booking.status,
        //       email: data?.email,
        //     });
        // }
        // Book rồi xóa khỏi gỉ hàng local
        cartItems.forEach(
          (item) => item.id && cartLocalStorage.remove(item.id),
        );
        if (!isAuthenticated && bsp.isUpgrade && data && data.length > 0) {
          window.location.href = "/mypage/booking?bookIds=" + data;
        } else {
          redirectToBooking();
        }
      },
      onError: (error: any) => {
        console.error("Update failed:", error);
        if (error?.code === 2415) {
          setErrorTitle("");
          setErrorDescription(t("Booking date time is not available"));
          errorBookingPopup.open();
        } else if (error?.code === 2416) {
          setErrorTitle("");
          setErrorDescription(t("The selected ticket is sold out"));
          errorBookingPopup.open();
        } else {
          setErrorTitle(t("Payment Failed Message"));
          setErrorDescription(t("Payment Failed Description"));
          errorBookingPopup.open();
        }
        setIsBooking(false);
      },
    },
  );

  const changeRequest = useMutation(
    (request: ChangeRequestDTO) => {
      return BookingService.changeRequest(request);
    },
    {
      onMutate: () => setIsBooking(true),
      onSuccess: (data) => {
        cartItems.forEach(
          (item) => item.id && cartLocalStorage.remove(item.id),
        );
        redirectToBooking();
      },
      onError: (error: any) => {
        console.error("Update failed:", error);
        alert(
          t(
            "An error occurred while processing your booking. " +
              (error?.message ?? ""),
          ),
        );
        setIsBooking(false);
      },
    },
  );

  const [userForm, setUserForm] = useState<CustomerDTO>({
    lastName: billingAddress?.lastName ?? "",
    firstName: billingAddress?.firstName ?? "",
    koreanLastName: billingAddress?.koreanLastName ?? "",
    koreanFirstName: billingAddress?.koreanFirstName ?? "",
    phone: billingAddress?.phoneNumber ?? "",
    email: billingAddress?.email ?? "",
    callingCode: billingAddress?.callingCode ?? "+82",
    requirement: "",
    address: billingAddress?.address ?? "",
    confirmEmail: billingAddress?.confirmEmail ?? "",
  });

  useEffect(() => {
    if (billingAddress) {
      setUserForm((prev) => ({
        ...prev,
        lastName: billingAddress.lastName ?? "",
        firstName: billingAddress.firstName ?? "",
        koreanLastName: billingAddress.koreanLastName ?? "",
        koreanFirstName: billingAddress.koreanFirstName ?? "",
        phone: billingAddress.phoneNumber ?? "",
        email: billingAddress.email ?? "",
        confirmEmail:
          mode === SHOPPING_CART_MODE.UPDATE_BOOKING
            ? (billingAddress.email ?? "")
            : "",
        address: billingAddress.address ?? "",
        callingCode: billingAddress.callingCode ?? "+82",
      }));
    }
  }, [billingAddress]);

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePolicy, setAgreePolicy] = useState(false);

  const [isSaveBillingAddress, setIsSaveBillingAddress] = useState(false);

  const [errors, setErrors] = useState({
    confirmEmail: "",
  });

  const validateForm = useMemo(() => {
    const isValid = Object.values(errors).every((i) => !i);
    const validateUserForm =
      !!userForm.lastName &&
      !!userForm.firstName &&
      // !!userForm.firstName2 &&
      // !!userForm.lastName2 &&
      !!userForm.phone &&
      !!userForm.email &&
      !!userForm.confirmEmail;

    return validateUserForm && isValid;
  }, [userForm, errors]);

  const validateTermsAgreement = useMemo(() => {
    return agreeTerms && agreePolicy;
  }, [agreeTerms, agreePolicy]);

  const onSubmit = () => {
    // Kiểm tra điều khoản trước khi booking
    if (!validateTermsAgreement) {
      agreePopup.open();
      return;
    }
    startHandleBooking();
  };

  const startHandleBooking = () => {
    if (!isAuthenticated) {
      setPopupVisible(true);
      return;
    }

    handleBooking();
  };
  // const paymentRedirect = usePaymentRedirect();
  const handleBooking = async () => {
    if (!jj.bookingPrice.data?.paidAmount) {
      submitBooking("");
      return;
    }
    setPayProcessing(true);
  };

  const submitBooking = async (paymentGatewayToken: string) => {
    const paymentRequest = {
      paymentGatewayToken,
      // TODO: will be remove
      cardNumber: "4111111111111111",
      cvvNumber: "123",
      cardHolderName: "Test",
      dateCard: "20/09",
      voucherId: (jj.voucherId ?? "").toString(),
    };

    if (bsp.isEdit && isPass) {
      const a = getChangeRequestData({
        bookingId: cartItems[0]?.id!,
        bookings: jj.cartItems,
        isEdit: bsp.isEdit!!,
        isUpgrade: bsp.isUpgrade!!,
      });
      changeRequest.mutate({
        ...a,
        updateBookingRequest: {
          ...a.updateBookingRequest,
          bookingMetaDataRequest: {
            paymentRequest,
            customer: userForm,
            isSaveBillingAddress: false,
          },
        },
      });
      return;
    }

    // setIsBooking(true);
    // TODO: Submmit booking form
    console.log();
    console.log(userForm);
    // const jpPayRequest = {
    //   amount: "",
    //   goodName: "BOOKING",
    //   buyerName: userForm.lastName + userForm.firstName,
    //   buyerTel: userForm.phone,
    //   buyerEmail: userForm.email,
    // };
    const bookingRequests = cartItems.map((x) =>
      mappingBookingState2RequestDTO(x, {
        voucherId: jj.voucherId,
      }),
    );
    try {
      // // const a = await genProcessDataFromBooking({
      // //   jpPayRequest,
      // //   bookingRequests,
      // // });
      // // console.log(a);
      // // const { pgAuditGroupId } = await paymentRedirect.open(JSON.stringify(a));
      // let pgAuditGroupId = "zx13gf2b";

      const dto: AllBookingRequestDTO = {
        bookingRequestList: bookingRequests,
        bookingMetaDataRequest: {
          paymentRequest,
          customer: userForm,
          isSaveBillingAddress: isSaveBillingAddress,
        },
      };
      console.log("AllBookingRequestDTO", dto);
      saveBooking.mutate(dto);
    } catch (e) {
      console.error("Error", e);
    }
  };
  // const handleBooking = () => {
  //   const request: BookingRequest = {
  //     userId: undefined,
  //     bookingId: undefined,
  //     paymentDTO: formValues,
  //     customer: userForm,
  //     bookingInfoRequestList: cartItems.map((item) =>
  //       Utils.mapBookingInfoToBookingInfoRequest(item?.bookingInfo)
  //     ),
  //   };
  //   if (isUpgrade) {
  //     request.bookingId = cartItems[0]?.bookingInfo?.id;
  //     saveBooking.mutate(request);
  //   } else {
  //     const token = localStorage.getItem("token");
  //     if (token) {
  //       const decodedToken = jwtDecode(token) as JwtPayload;
  //       if (decodedToken) {
  //         console.log("decodedToken", decodedToken);
  //         request.userId = Number(decodedToken.sub);
  //       }
  //     }
  //     mutation.mutate(request);
  //   }
  // };

  const closePopup = () => {
    setPopupVisible(false);
  };

  const validate = (name: string, value: string) => {
    let error = "";
    switch (name) {
      case "cardNumber":
        const cardNumberRegex = /^[0-9]{16}$/;
        error = cardNumberRegex.test(value.replace(/\s/g, ""))
          ? ""
          : t("Invalid information");
        break;
      case "dateCard":
        const expiryRegex = /^(0[1-9]|1[0-2])\/\d{2}$/;
        error = expiryRegex.test(value) ? "" : t("Invalid information");
        break;
      case "cvvNumber":
        const cvvRegex = /^[0-9]{3}$/;
        error = cvvRegex.test(value) ? "" : t("Invalid information");
        break;
      case "cardHolderName":
        error = value.trim() === "" ? t("Invalid information") : "";
        break;
      default:
        break;
    }
    if (!error) {
      setisActiveInfoBank(true);
    }
    return error;
  };

  const handleChangeUserInfo = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setUserForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (
      userForm.email &&
      userForm.confirmEmail &&
      userForm.email !== userForm.confirmEmail
    ) {
      setErrors((prev) => ({
        ...prev,
        confirmEmail: t("The email addresses do not match"),
      }));
    } else {
      setErrors((prev) => ({ ...prev, confirmEmail: "" }));
    }
  }, [userForm]);

  return (
    <div className="flex w-full flex-col gap-[1.5rem] self-center sm:w-[40%]">
      {isBooking && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-30">
          <div className="flex items-center justify-center rounded-lg bg-white px-8 py-6 shadow-lg">
            <span className="text-center text-lg font-semibold text-gray-900">
              <Trans i18nKey={t("Your booking is being processed...")} />
            </span>
          </div>
        </div>
      )}
      {payProcessing && (
        <SquarePayPopup
          onOk={(x) => submitBooking(x.token)}
          onClose={() => setPayProcessing(false)}
        />
      )}
      {/* block 1 */}
      <div className="form-1 flex flex-col justify-center gap-[1.88rem] rounded-[1.25rem] border border-solid border-gray-300 p-4 sm:p-6">
        <div className="items-start justify-center gap-[0.38rem]">
          <UiH4 text={t("Information")} />
          <UiTextXs
            text={t("Enter the representative customer info when booking")}
          />
        </div>

        <div className="flex items-center justify-center gap-[0.625rem]">
          <div className="w-full">
            <p className="mb-2 text-sm text-gray-900 sm:text-base">
              <span className="text-gray-900">{t("Korean name")}</span>
            </p>

            <input
              className="bg-white-a700 w-full rounded-[0.625rem] border border-solid border-gray-300 p-3 text-gray-900 focus:outline-none"
              type="text"
              name="koreanLastName"
              value={userForm.koreanLastName}
              onChange={(e) => handleChangeUserInfo(e)}
            />
          </div>
          {/* <div className="">
            <p className="mb-2 text-sm text-gray-900 sm:text-base">
              <span className="text-gray-900">{t("Korean First name")}</span>
            </p>

            <input
              className="bg-white-a700 w-full rounded-[0.625rem] border border-solid border-gray-300 p-3 text-gray-900 focus:outline-none"
              type="text"
              name="koreanFirstName"
              value={userForm.koreanFirstName}
              onChange={(e) => handleChangeUserInfo(e)}
            />
          </div> */}
        </div>

        <div className="flex items-center justify-center gap-[0.625rem]">
          <div className="w-full">
            <p className="mb-2 text-sm text-gray-900 sm:text-base">
              <span className="text-gray-900">{t("English last name")}</span>
              <span className="text-red-600">*</span>
            </p>

            <input
              className="bg-white-a700 w-full rounded-[0.625rem] border border-solid border-gray-300 p-3 text-gray-900 focus:outline-none"
              type="text"
              name="lastName"
              value={userForm.lastName}
              onChange={(e) => handleChangeUserInfo(e)}
            />
          </div>
          <div className="w-full">
            <p className="mb-2 text-sm text-gray-900 sm:text-base">
              <span className="text-gray-900">{t("English first name")}</span>
              <span className="text-red-600">*</span>
            </p>

            <input
              className="bg-white-a700 w-full rounded-[0.625rem] border border-solid border-gray-300 p-3 text-gray-900 focus:outline-none"
              type="text"
              name="firstName"
              value={userForm.firstName}
              onChange={(e) => handleChangeUserInfo(e)}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-[0.625rem]">
          <div className="flex">
            <p className="text-sm text-gray-900 sm:text-base">
              <span className="text-gray-900">{t("Email")}</span>
              <span className="text-red-600">*</span>
            </p>
          </div>
          <input
            className="bg-white-a700 flex h-[3.13rem] cursor-text items-center justify-center rounded-[0.625rem] border border-solid border-gray-300 px-[0.88rem] text-sm text-gray-900 focus:border-[#DCDCDC] focus:outline-none sm:text-base"
            type="email"
            name="email"
            value={userForm.email}
            onChange={(e) => handleChangeUserInfo(e)}
            placeholder="honggildong@gmail.com"
          />
        </div>

        <div className="flex w-full flex-col gap-[0.625rem]">
          <div className="flex">
            <p className="text-sm text-gray-900 sm:text-base">
              <span className="text-gray-900">{t("Confirm Email")}</span>
              <span className="text-red-600">*</span>
            </p>
          </div>
          <input
            className="bg-white-a700 flex h-[3.13rem] cursor-text items-center justify-center rounded-[0.625rem] border border-solid border-gray-300 px-[0.88rem] text-sm text-gray-900 focus:border-[#DCDCDC] focus:outline-none sm:text-base"
            type="email"
            name="confirmEmail"
            value={userForm.confirmEmail}
            onChange={(e) => handleChangeUserInfo(e)}
            placeholder="honggildong@gmail.com"
          />
          {userForm.confirmEmail && errors.confirmEmail && (
            <span className="text-red-600">{errors.confirmEmail}</span>
          )}
        </div>

        <div className="flex w-full items-center justify-start gap-[0.625rem]">
          <div className="flex w-full flex-col">
            <p className="mb-2 w-full text-sm text-gray-900 sm:text-base">
              <span className="text-gray-900">{t("Phone number")}</span>
              <span className="text-red-600">*</span>
            </p>
            <div className="flex gap-[0.625rem]">
              <div
                className="callingCode relative"
                data-code={userForm.callingCode}
              >
                <select
                  autoComplete="none"
                  value={userForm.callingCode}
                  name="callingCode"
                  onChange={(e) => handleChangeUserInfo(e)}
                  className="bg-white-a700 text-gray-900x mb-0 h-full w-[7.5rem] select-none rounded-[0.625rem] border border-solid border-gray-300 p-3 text-white focus:border-[#DCDCDC] focus:outline-none"
                >
                  {COUNTRY_CODE.map((code) => (
                    <option
                      className="text-gray-900"
                      key={code.countryCode + code.countryName}
                      value={code.countryCode}
                    >
                      {`${code.countryName} (${code.countryCode})`}
                    </option>
                  ))}
                </select>
              </div>

              <input
                autoComplete="off"
                onChange={(e) => handleChangeUserInfo(e)}
                className="bg-white-a700 w-full rounded-[0.625rem] border border-solid border-gray-300 p-3 text-gray-900 focus:border-[#DCDCDC] focus:outline-none"
                type="text"
                value={userForm.phone}
                name="phone"
              />
            </div>
          </div>
        </div>
        {/* <div className="flex w-full flex-col gap-[0.63rem]">
          <div className="flex">
            <p className="text-sm text-gray-900 sm:text-base">
              {t("Address ")}({t("optional")})
            </p>
          </div>
          <label className="bg-white-a700 flex h-[3.13rem] cursor-text items-center justify-center rounded-[0.625rem] border border-solid border-gray-300 px-[0.88rem] text-sm text-gray-900 sm:text-base">
            <input
              onChange={(e) => handleChangeUserInfo(e)}
              className="w-full focus:border-[#DCDCDC] focus:outline-none"
              type="text"
              name="address"
              value={userForm.address}
              placeholder={t("Please enter your address")}
            />
          </label>
        </div> */}
        <div className="flex w-full flex-col gap-[1.75rem]">
          <div className="flex flex-col gap-[1.00rem]">
            <div>
              <div className="flex items-center gap-[0.69rem]">
                <UiH4 text={t("Requirement")} />
                <h4 className="text-xs font-semibold text-gray-900 sm:text-base">
                  ({t("optional")})
                </h4>
              </div>
              {/* <UiTextXs
                text={t(
                  "The request may not be accommodated depending on the service",
                )}
              /> */}
            </div>
            <textarea
              className="bg-white-a700 h-[8.75rem] rounded-[0.75rem] border border-solid !border-gray-300 p-[0.75rem] px-[0.75rem] text-sm text-gray-600 focus:border-[#DCDCDC] focus:outline-none sm:text-base"
              name="requirement"
              onChange={(e) => handleChangeUserInfo(e)}
              placeholder={t("Please enter your request")}
            ></textarea>
          </div>
        </div>
        <div className="term-box flex w-full flex-col gap-[1.75rem]">
          <div className="flex flex-col items-start gap-[0.38rem]">
            <UiH4 text={t("Please check before purchase")} />
            <div className="w-full rounded-[0.625rem] border border-solid border-gray-300 shadow-sm">
              <label className="flex w-full cursor-text items-center justify-start gap-[0.75rem] self-stretch pb-2 pl-[1.50rem] pr-[0.75rem] pt-5 text-[0.88rem] text-gray-900">
                <input
                  className="h-[1.25rem] w-[1.25rem] rounded border border-solid border-gray-300 checked:border-[0.1875rem] checked:border-solid checked:border-gray-300 checked:bg-gray-300 checked:hover:border-gray-300 checked:hover:bg-gray-300 checked:focus:border-gray-300 checked:focus:bg-gray-300"
                  type="checkbox"
                  name="Billing Address Checkbox"
                  id="BillingAddressCheckbox"
                  checked={agreeTerms}
                  onChange={() => setAgreeTerms(!agreeTerms)}
                />
                <a
                  href="/p/termPolicy/travelTermsAndConditions"
                  target="_blank"
                  className="underline-offset-3 w-full cursor-pointer underline hover:text-blue-500"
                >
                  {t("I agree with the terms and conditions.")}
                </a>
              </label>
              <label className="flex w-full cursor-text items-center justify-start gap-[0.75rem] self-stretch pb-5 pl-[1.50rem] pr-[0.75rem] pt-2 text-[0.88rem] text-gray-900">
                <input
                  className="h-[1.25rem] w-[1.25rem] rounded border border-solid border-gray-300 checked:border-[0.1875rem] checked:border-solid checked:border-gray-300 checked:bg-gray-300 checked:hover:border-gray-300 checked:hover:bg-gray-300 checked:focus:border-gray-300 checked:focus:bg-gray-300"
                  type="checkbox"
                  name="Billing Address Checkbox"
                  id="BillingAddressCheckbox"
                  checked={agreePolicy}
                  onChange={() => setAgreePolicy(!agreePolicy)}
                />
                <a
                  href="/p/termPolicy/cancelAndRefundPolicy"
                  target="_blank"
                  className="underline-offset-3 w-full cursor-pointer underline hover:text-blue-500"
                >
                  {t("I agree with the refund/cancel policy.")}
                </a>
              </label>
            </div>
          </div>
        </div>

        {/* <div className="flex w-full items-center justify-start gap-[0.625rem]">
          <div className="flex w-full flex-col">
            <div className="flex items-center gap-[0.31rem] text-sm text-gray-900 sm:text-base">
              <input
                className="h-[1.25rem] w-[1.25rem] rounded border border-solid border-gray-300 checked:border-[0.1875rem] checked:border-solid checked:border-gray-300 checked:bg-gray-300 checked:hover:border-gray-300 checked:hover:bg-gray-300 checked:focus:border-gray-300 checked:focus:bg-gray-300"
                type="checkbox"
                name="Billing Address Checkbox"
                id="saveCheckbox"
                checked={isSaveBillingAddress}
                onChange={() => setIsSaveBillingAddress(!isSaveBillingAddress)}
              />
              <label htmlFor="saveCheckbox" className="cursor-pointer">
                {t("Save this billing address")}
              </label>
            </div>
          </div>
        </div> */}
      </div>

      {/* block 2 */}
      <div className="flex flex-col gap-[1.88rem] rounded-[1.25rem] border border-solid border-gray-300 p-4 sm:p-[1.88rem]">
        <div className="flex flex-col gap-[2.00rem]">
          <VoucherBox
            productIds={productIds}
            voucherId={jj.voucherId}
            onApply={(voucherId) => {
              jj.setVoucherId(voucherId);
            }}
          />
        </div>

        <CartPriceDetail bookingPrice={jj.bookingPrice} />

        <button
          disabled={createBooking.isLoading || !validateForm}
          onClick={onSubmit}
          className={`${
            createBooking.isLoading || !validateForm
              ? "bg-[#BFC5C9]"
              : "bg-gray-900"
          } flex h-[3.13rem] cursor-pointer flex-row items-center justify-center self-stretch whitespace-nowrap rounded-[1.5rem] px-[2.13rem] text-center text-sm font-semibold text-white sm:text-base`}
        >
          {createBooking.isLoading ? t("Booking...") : t("Book")}
        </button>
        {isAdminRole && (
          <button
            onClick={() => {
              if (!validateTermsAgreement) {
                agreePopup2.open();
                return;
              }
              submitBooking("CASH");
            }}
            disabled={createBooking.isLoading || !validateForm}
            className={`${
              createBooking.isLoading || !validateForm
                ? "bg-[red]"
                : "bg-[#be1919]"
            } flex h-[3.13rem] cursor-pointer flex-row items-center justify-center self-stretch whitespace-nowrap rounded-[1.5rem] px-[2.13rem] text-center text-sm font-semibold text-white sm:text-base`}
          >
            Admin books with cash
          </button>
        )}
      </div>
      {isPopupVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="popup-modal w-[22.5rem] gap-[1.5rem] rounded-[1.25rem] bg-white p-[2rem] pb-[1rem] pt-[1.5rem]">
            <div className="text-center">
              <div className="mb-1 flex justify-center">
                <img
                  src={icAsset("../../../assets/icons/question.svg")}
                  className="h-[3rem] w-[3rem] rounded-[0.5rem] object-cover"
                  alt="question icon"
                />
              </div>
              <p className="mb-[1.5rem] text-lg">
                {t("Do you want to book without logging in")}
              </p>
              <div className="flex justify-between space-x-[1rem]">
                <button
                  className="h-[3rem] w-[18.5rem] rounded-[1.953rem] bg-[#E9EAEB] text-base text-[#232323]"
                  onClick={() => {
                    closePopup();
                    handleOpenLogin();
                  }}
                >
                  {t("Login")}
                </button>
                <button
                  className="h-[3rem] w-[18.5rem] rounded-[1.953rem] bg-primary text-base text-[#232323]"
                  onClick={() => {
                    closePopup();
                    handleBooking();
                  }}
                >
                  {t("Book as Guest")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {agreePopup.isOpen && (
        <PopupAgree
          onClose={agreePopup.close}
          onAgree={() => {
            setAgreeTerms(true);
            setAgreePolicy(true);
            agreePopup.close();

            startHandleBooking();
          }}
        />
      )}
      {agreePopup2.isOpen && (
        <PopupAgree
          onClose={agreePopup2.close}
          onAgree={() => {
            setAgreeTerms(true);
            setAgreePolicy(true);
            agreePopup2.close();

            submitBooking("CASH");
          }}
        />
      )}
      {errorBookingPopup.isOpen && (
        <ErrorPopup
          onClose={errorBookingPopup.close}
          message={errorTitle}
          description={errorDescription}
          buttonText={t("Ok")}
        />
      )}
    </div>
  );
};

export default FormBookOrderProduct;
